import { useId, type FormEvent, type FormHTMLAttributes, type ReactNode } from 'react';

import { ApiError } from '../../api';

/**
 * Form primitives.
 *
 * Two things every form in the product needs and no form should re-invent:
 *
 *  - A label bound to its input by id, an error bound to it by
 *    `aria-describedby`, and `aria-invalid` when it is wrong. Typed by hand,
 *    one of the three is always missing.
 *  - Somewhere for the server's field-level errors to land. The API returns
 *    `details` with a dotted `field` path on every validation failure, so a
 *    rejected form can point at the field that caused it instead of showing one
 *    sentence at the top and leaving the person to guess.
 */

export function Form({
  onSubmit,
  children,
  ...rest
}: {
  onSubmit: () => void;
  children: ReactNode;
} & Omit<FormHTMLAttributes<HTMLFormElement>, 'onSubmit' | 'children'>) {
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit();
  };
  return (
    <form className="form" noValidate onSubmit={handleSubmit} {...rest}>
      {children}
    </form>
  );
}

export function TextField({
  label,
  value,
  onChange,
  type = 'text',
  error,
  hint,
  autoComplete,
  required = false,
  disabled = false,
  inputMode,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  // `date` and `month` are here rather than in fields of their own because the
  // browser's own inputs are already localised, already keyboard-accessible and
  // already know what a calendar is. What they needed was the label, the error
  // and the `aria-describedby` wiring every other field in the product gets.
  // `month` in particular is the shape a submission cycle really has: the API
  // writes one `YYYY-MM`, which is exactly what this input produces.
  type?: 'text' | 'email' | 'password' | 'date' | 'month';
  error?: string | undefined;
  hint?: string;
  autoComplete?: string;
  required?: boolean;
  disabled?: boolean;
  inputMode?: 'text' | 'numeric';
}) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy =
    [error !== undefined ? errorId : null, hint !== undefined ? hintId : null]
      .filter((part): part is string => part !== null)
      .join(' ') || undefined;

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
        {required && (
          <span className="field__required" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {hint !== undefined && (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      )}
      <input
        id={id}
        className={error !== undefined ? 'field__input field__input--invalid' : 'field__input'}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={error !== undefined}
        aria-describedby={describedBy}
        required={required}
        disabled={disabled}
        {...(autoComplete !== undefined && { autoComplete })}
        {...(inputMode !== undefined && { inputMode })}
      />
      {error !== undefined && (
        <p className="field__error" id={errorId}>
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * One choice out of a list, and never two.
 *
 * A `select` rather than a set of checkboxes is the whole point where the
 * portal's change form is concerned: the insurer expresses a movement as three
 * mutually exclusive spreadsheet columns, which is a shape in which "admission
 * *and* termination" is something a person can tick. A single-valued control
 * cannot express it, so the rule is a property of the widget rather than of a
 * validator somebody has to remember to write.
 */
export function SelectField<TValue extends string>({
  label,
  value,
  onChange,
  options,
  placeholder,
  error,
  hint,
  required = false,
  disabled = false,
}: {
  label: string;
  value: TValue | '';
  onChange: (value: TValue | '') => void;
  options: readonly { readonly value: TValue; readonly label: string }[];
  /** The "nothing chosen yet" entry. Omit when a value is always selected. */
  placeholder?: string;
  error?: string | undefined;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
}) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy =
    [error !== undefined ? errorId : null, hint !== undefined ? hintId : null]
      .filter((part): part is string => part !== null)
      .join(' ') || undefined;

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
        {required && (
          <span className="field__required" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {hint !== undefined && (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      )}
      <select
        id={id}
        className={error !== undefined ? 'field__input field__input--invalid' : 'field__input'}
        value={value}
        onChange={(event) => onChange(event.target.value as TValue | '')}
        aria-invalid={error !== undefined}
        aria-describedby={describedBy}
        required={required}
        disabled={disabled}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error !== undefined && (
        <p className="field__error" id={errorId}>
          {error}
        </p>
      )}
    </div>
  );
}

export function FileField({
  label,
  onChange,
  accept,
  hint,
  error,
  required = false,
  disabled = false,
}: {
  label: string;
  /** The chosen file, or `null` when the person cleared the input. */
  onChange: (file: File | null) => void;
  accept?: string;
  hint?: string;
  error?: string | undefined;
  required?: boolean;
  disabled?: boolean;
}) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy =
    [error !== undefined ? errorId : null, hint !== undefined ? hintId : null]
      .filter((part): part is string => part !== null)
      .join(' ') || undefined;

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
        {required && (
          <span className="field__required" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {hint !== undefined && (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      )}
      <input
        id={id}
        className={error !== undefined ? 'field__input field__input--invalid' : 'field__input'}
        type="file"
        // Uncontrolled on purpose: a file input's value cannot be set from
        // script, so pretending it is controlled would be a lie that breaks the
        // first time somebody re-picks the same file.
        onChange={(event) => onChange(event.target.files?.[0] ?? null)}
        aria-invalid={error !== undefined}
        aria-describedby={describedBy}
        required={required}
        disabled={disabled}
        {...(accept !== undefined && { accept })}
      />
      {error !== undefined && (
        <p className="field__error" id={errorId}>
          {error}
        </p>
      )}
    </div>
  );
}

export function SubmitButton({
  children,
  pending = false,
  pendingLabel = 'Enviando…',
}: {
  children: ReactNode;
  pending?: boolean;
  pendingLabel?: string;
}) {
  return (
    <button type="submit" className="button button--primary" disabled={pending}>
      {pending ? pendingLabel : children}
    </button>
  );
}

/**
 * Whatever went wrong with the submission, above the form.
 *
 * `role="alert"` so it is announced when it appears, because a person who has
 * just pressed a button is looking at the button, not at the top of the page.
 *
 * Only our own pt-BR sentence is shown. The API's `details` carry Pydantic's
 * English prose ("String should have at least 1 character"), which is written
 * for whoever is integrating and would be the one thing on screen in the wrong
 * language. What is useful from `details` is *which field* was rejected, and
 * that is machine-readable -- see `fieldErrors`, which puts a pt-BR message on
 * the field itself.
 *
 * `message` overrides that sentence for a form that knows what a code means
 * *here*. The per-code copy in `errors.ts` has to hold for every endpoint that
 * can return it, so it is necessarily general: a `conflict` becomes "esta
 * informação foi alterada por outra pessoa", which is right for a stale write
 * and wrong for a unique column that is already taken. A screen calling one
 * endpoint knows which of those it is, and telling somebody to reload a page
 * that is not stale is worse than saying nothing. The correlation id is still
 * rendered, because support asks for it whatever the sentence says.
 */
export function FormError({ error, message }: { error: unknown; message?: string | undefined }) {
  if (error === null || error === undefined) {
    return null;
  }
  const apiError = error instanceof ApiError ? error : null;
  return (
    <div className="form__error" role="alert">
      <p>{message ?? apiError?.message ?? 'Algo deu errado. Tente de novo em instantes.'}</p>
      {apiError?.correlationId !== undefined && (
        <p className="form__error-reference">
          Código de referência: <code>{apiError.correlationId}</code>
        </p>
      )}
    </div>
  );
}
